import React from 'react';
import moment from 'moment';

import BedConfig from './enums/BedConfig';
import StayType from './enums/StayType';
import { formatPrice } from './utils';
import { DATE_FORMAT } from './constants';

/**
 * Funkce vrací options typů stravy pro použití v selectu.
 * @param boardBases
 * @param currency
 * @returns {Array}
 */
export function getBoardBaseOptions(boardBases, currency) {
	const options = [];

	Object.entries(boardBases).forEach(([id, boardBase]) => {
		let label = boardBase.name;
		const formattedPrice = getBoardBasePrice(boardBase, currency);
		const price = getBoardBasePrice(boardBase, currency, false);

		if (price) {
			label += ` (+ ${formattedPrice})`;
		}

		options.push({
			value: id,
			label,
			price: formattedPrice,
		})
	});

	return options;
}

export function getBoardBasePrice(boardBase, currency, formatted = true) {
	if (boardBase.priceIncluded !== 1 && boardBase.price && boardBase.price[currency.id]) {
		return formatted ? formatPrice(boardBase.price[currency.id], currency) : boardBase.price[currency.id];
	}

	return formatted ? formatPrice(0, currency) : 0;
}

/**
 * Funkce vrací celkovou cenu za stravu v závislosti na počtu hostů a nocí.
 * @param{Object} boardBase
 * @param {Object} guestTypes
 * @param {Object} rooms
 * @param {number} noOfNights
 * @param {bool} applyGuestTypeDiscountToBoardBase
 * @param {Object} currency
 * @param {bool} formatted
 * @returns {*}
 */
export function getBoardBaseTotalPrice(boardBase, guestTypes, rooms, noOfNights, applyGuestTypeDiscountToBoardBase, currency, formatted = true) {
	let price = 0;
	if (boardBase.priceIncluded !== 1 && boardBase.price && boardBase.price.hasOwnProperty(currency.id)) {
		const boardBasePrice = boardBase.price[currency.id];
		if (applyGuestTypeDiscountToBoardBase) {
			// Na stravu uplatníme slevu dle typu hosta
			for (let roomType of Object.keys(rooms)) {
				// Pro každý vybraný typ pokoje
				for (let room of rooms[roomType]) {
					// Pro každý vybraný konkrétní pokoj
					for (let guestType of Object.keys(room.guestTypes)) {
						if (!guestTypes.hasOwnProperty(guestType) || !guestTypes[guestType].exclude) {
							// Pro každý typ hosta
							let guestTypeBoardBaseTotalPrice = boardBasePrice * room.guestTypes[guestType] * noOfNights * (1 - guestTypes[guestType].boardBaseDiscount / 100);
							if (guestTypes[guestType].fixedDiscount) {
								price += guestTypeBoardBaseTotalPrice - room.guestTypes[guestType] * guestTypes[guestType].discount[currency.id] * (guestTypes[guestType].dailyDiscount ? noOfNights : 1);
							} else {
								price += guestTypeBoardBaseTotalPrice * (1 - guestTypes[guestType].discount / 100);
							}
						}
					}
				}
			}
		} else {
			// Na stravu neuplatňujeme slevu dle typu hosta
			for (let roomType of Object.keys(rooms)) {
				for (let room of rooms[roomType]) {
					price += boardBasePrice * Object.keys(room.guestTypes)
						.reduce((accumulator, guestTypeId) => accumulator + (guestTypes.hasOwnProperty(guestTypeId) && guestTypes[guestTypeId].exclude ? 0 : room.guestTypes[guestTypeId]) * (1 - guestTypes[guestTypeId].boardBaseDiscount / 100), 0);
				}
			}
		}
	}

	return formatted ? formatPrice(price, currency) : price;
}

/**
 * Funkce vrací celkovou cenu za individuální stravu v závislosti na počtu hostů a nocí.
 * @param {Object} boardBases
 * @param {Object} selectedBoardBases
 * @param {Object} guestTypes
 * @param {number} noOfNights
 * @param {bool} applyGuestTypeDiscountToBoardBase
 * @param {Object} currency
 * @param {bool} formatted
 * @returns {(number|string|null)}
 */
export function getBoardBasesTotalPrice(boardBases, selectedBoardBases, guestTypes, noOfNights, applyGuestTypeDiscountToBoardBase, currency, formatted = true) {
	if (selectedBoardBases == null) return null;

	let price = 0;

	for (let boardBase of Object.keys(selectedBoardBases)) {
		if (boardBases.hasOwnProperty(boardBase)) {
			price += getBoardBasesTotalBoardBasePrice(boardBases[boardBase], selectedBoardBases, guestTypes, noOfNights, applyGuestTypeDiscountToBoardBase, currency, false);
		}
	}

	return formatted ? formatPrice(price, currency) : price;
}

/**
 * Funkce vrací cenu za individuální stravu v závislosti na počtu hostů a nocí.
 * @param {Object} boardBase
 * @param {Object} selectedBoardBases
 * @param {Object} guestTypes
 * @param {number} noOfNights
 * @param {bool} applyGuestTypeDiscountToBoardBase
 * @param {Object} currency
 * @param {bool} formatted
 * @returns {(number|string)}
 */
export function getBoardBasesTotalBoardBasePrice(boardBase, selectedBoardBases, guestTypes, noOfNights, applyGuestTypeDiscountToBoardBase, currency, formatted = true) {
	let price = 0;

	if (boardBase.priceIncluded !== 1 && boardBase.price && boardBase.price.hasOwnProperty(currency.id)) {
		let boardBaseTotalPrice = boardBase.price[currency.id] * noOfNights;

		if (applyGuestTypeDiscountToBoardBase) {
			// Na stravu uplatníme slevu dle typu hosta
			for (let guestType of Object.keys(selectedBoardBases[boardBase.id])) {
				if (selectedBoardBases[boardBase.id][guestType] > 0 && (!guestTypes.hasOwnProperty(guestType) || !guestTypes[guestType].exclude)) {
					price += selectedBoardBases[boardBase.id][guestType]
						* (1 - guestTypes[guestType].boardBaseDiscount / 100)
						* (
							guestTypes[guestType].fixedDiscount
								? boardBaseTotalPrice - guestTypes[guestType].discount[currency.id] * (guestTypes[guestType].dailyDiscount ? noOfNights : 1)
								: boardBaseTotalPrice * (1 - guestTypes[guestType].discount / 100)
						);
				}
			}
		} else {
			// Na stravu neuplatňujeme slevu dle typu hosta
			price += boardBaseTotalPrice * Object.keys(selectedBoardBases[boardBase.id])
				.reduce((accumulator, guestTypeId) => accumulator + (guestTypes.hasOwnProperty(guestTypeId) && guestTypes[guestTypeId].exclude ? 0 : selectedBoardBases[boardBase.id][guestTypeId]) * (1 - guestTypes[guestTypeId].boardBaseDiscount / 100), 0);
		}
	}

	return formatted ? formatPrice(price, currency) : price;
}

/**
 * Funkce vrací výchozí typ stravy.
 * @param boardBases
 * @returns {?number}
 */
export function getDefaultBoardBase(boardBases) {
	if (!boardBases) return null;

	let defaultBoardBase = null;

	Object.values(boardBases).forEach((boardBase) => {
		if (!defaultBoardBase) {
			defaultBoardBase = boardBase.id;
		}
		if (boardBase.default) {
			defaultBoardBase = boardBase.id;
		}
	});

	return defaultBoardBase;
}

/**
 * Funkce vrací výchozí typ individuální stravy.
 * @param {Object} boardBases
 * @param {Object} guestTypes
 * @param {Object} selectedRooms
 * @returns {Object}
 */
export function getDefaultBoardBases(boardBases, guestTypes, selectedRooms) {
	if (!boardBases) return null;

	const defaultBoardBase = getDefaultBoardBase();
	let defaultBoardBases = {};

	for (let boardBase of Object.values(boardBases)) {
		defaultBoardBases[boardBase.id] = {};
		for (let guestType of Object.values(guestTypes)) {
			defaultBoardBases[boardBase.id][guestType.id] = 0;
		}
		if (boardBase.id === defaultBoardBase) {
			for (let rooms of Object.values(selectedRooms)) {
				for (let room of rooms) {
					for (let roomGuestTypeId of Object.keys(room.guestTypes)) {
						defaultBoardBases[boardBase.id][roomGuestTypeId] += room.guestTypes[roomGuestTypeId];
					}
				}
			}
		}
	}

	return defaultBoardBases;
}

/**
 * @param {{daily: boolean, price: number|Object.<string, number>}} extraFee
 * @param {number} noOfNights
 * @param {number|null} accommodationPrice celková cena za ubytování v měně currency pro výpočet výsledné ceny
 *     za příplatek/slevu v případě, že je cena za příplatek/slevu zadaná procentuálně
 * @param {Object} currency
 * @param {boolean} formatted
 * @param {boolean} abs
 * @returns {number|string}
 */
export function getExtraFeePrice(extraFee, noOfNights, accommodationPrice, currency, formatted = true, abs = false) {
	if (extraFee.price) {
		if (typeof extraFee.price === 'object') {
			// cena zadaná pevnou částkou
			if (extraFee.price[currency.id]) {
				let price = extraFee.price[currency.id] * (extraFee.daily ? noOfNights : 1);
				if (abs) {
					price = Math.abs(price);
				}
				return formatted ? formatPrice(price, currency) : price;
			}
		} else {
			// cena zadaná procentem z ceny ubytování
			if (accommodationPrice !== null) {
				let price = accommodationPrice * extraFee.price / 100;
				if (abs) {
					price = Math.abs(price);
				}
				return formatted ? formatPrice(price, currency) : price;
			} else {
				let price = extraFee.price;
				if (abs) {
					price = Math.abs(price);
				}
				return formatted ? price + ' %' : price;
			}
		}
	}

	return formatted ? formatPrice(0, currency) : 0;
}

/**
 * @param {Object} selectedRooms
 * @param {Object} guestTypes
 * @param {{id: number, daily: boolean, price: number|Object.<string, number>}} extraFee
 * @param {boolean} auto automatický příplatek/sleva?
 * @param {number} noOfNights
 * @param {Object} currency
 * @param {boolean} formatted
 * @returns {number|string}
 */
export function getTotalExtraFeePrice(selectedRooms, guestTypes, extraFee, auto, noOfNights, currency, formatted = true) {
	let totalPrice = 0;

	for (const rooms of Object.values(selectedRooms)) {
		for (const room of rooms.filter(room => auto || (room.extraFees !== undefined && room.extraFees[extraFee.id]))) {
			const roomPrice = getRoomPrice(room, currency, room.guestTypes, guestTypes);
			totalPrice += getExtraFeePrice(extraFee, noOfNights, roomPrice, currency, false);
		}
	}

	return formatted ? formatPrice(totalPrice, currency) : totalPrice;
}

/**
 * @param {Object} formValues
 * @returns {Object}
 */
export function getSelectedExtraFees(formValues) {
	const selectedExtraFees = {};

	for (const rooms of Object.values(formValues.rooms)) {
		for (const room of rooms.filter(room => room.extraFees !== undefined)) {
			for (const extraFeeId in room.extraFees) {
				if (room.extraFees[extraFeeId]) {
					selectedExtraFees[extraFeeId] = (selectedExtraFees[extraFeeId] || 0) + 1;
				}
			}
		}
	}

	return selectedExtraFees;
}

/**
 * @param {Object} formValues
 * @returns {boolean}
 */
export function isSelectedExtraFee(formValues) {
	for (const rooms of Object.values(formValues.rooms)) {
		if (Object.values(rooms).some(room => room.extraFees !== undefined && room.extraFees.includes(true))) {
			return true;
		}
	}
	return false;
}

/**
 * Funkce vrací kompletní strukturu daného typu pokoje podle jeho ID.
 * @param roomTypeId
 * @param roomOffers
 * @returns {*}
 */
export function getRoomOfferByRoomTypeId(roomTypeId, roomOffers) {
	roomTypeId = roomTypeId.startsWith('_') ? roomTypeId : parseInt(roomTypeId, 10);
	for (let roomOffer of roomOffers) {
		if (roomOffer.id === roomTypeId) {
			return roomOffer;
		}
	}

	return null;
}

/**
 * Funkce vrací celkový počet hostů dle zvolené kategorie hosta.
 * @param {Object} formValues
 * @param {string} guestType
 * @returns {number}
 */
export function getNoOfSelectedGuestsByGuestType(formValues, guestType) {
	return Object.values(formValues.rooms).reduce(
		(accumulator, rooms) => accumulator + rooms.reduce(
			(accumulator2, room) => accumulator2 + room.guestTypes[guestType], 0
		), 0
	);
}

/**
 * Funkce dostane jako parametr odpověď serveru, vytáhne z něj typy pokojů a jednotlivé pokoje a vytvoří pole,
 * které se použije pro vykreslení pokojů ve formuláři.
 * @param response {object}
 * @returns {Array}
 */
export function processRoomTypes(response) {
	const roomOffers = [];

	for (let id in response.roomTypes) {
		if (!response.roomTypes.hasOwnProperty(id)) continue;

		const roomType = response.roomTypes[id];

		roomOffers.push({
			...roomType,
			id: id.startsWith('_') ? id : parseInt(id, 10), // pokud id začíná znakem podtržítka, nejedná se typ pokoje, ale o konkrétní pokoj
			noOfVacant: Object.values(roomType.rooms).filter(room => room.available).length,
			bedConfig: getBedConfiguration(roomType),
		});
	}

	roomOffers.sort((a, b) => a.priority - b.priority); // seřadíme pokoje vzestupně podle priority

	return roomOffers;
}

/**
 * Funkce vrací cenu za pokoj v závislosti na počtu hostů.
 * @param room
 * @param currency
 * @param guests int|object
 * @param guestTypes
 * @param boardBases
 * @param {{daily: boolean, price: number|Object.<string, number>}[]|null} extraFees automatické příplatky a slevy
 * @param pricelistFees
 * @param noOfNights
 * @returns {*}
 */
export function getRoomPrice(room, currency, guests = null, guestTypes = null, boardBases = null, extraFees = null, pricelistFees = null, noOfNights = 1) {
	if (guests === 0 || !room || !room.prices || !room.prices[currency.id]) {
		return 0;
	}

	const prices = room.prices;
	const currencyPrices = prices[currency.id];

	const keys = Object.keys(currencyPrices);
	const maxNoOfGuests = keys[keys.length - 1];

	// Pokud v guests neposíláme pouze počet hostů (integer), ale přesné počty podle jednotlivých kategorií
	if (typeof guests === 'object' && guestTypes) {
		// Celkový počet hostů započítavaných do ceny ubytování
		const noOfGuests = Object.keys(guests)
			.reduce((accumulator, guestTypeId) => accumulator + (guestTypes.hasOwnProperty(guestTypeId) && guestTypes[guestTypeId].exclude ? 0 : guests[guestTypeId]), 0);

		// Tohle by nemělo nastat
		if (noOfGuests === 0 || noOfGuests > maxNoOfGuests) {
			return 0;
		}

		/*
		const guestTypeIdsWithDiscounts = Object.keys(guests)
			.filter(guestTypeId => guests[guestTypeId] > 0)
			.filter(guestTypeId => guestTypes[guestTypeId].discount > 0)
			.map(guestTypeId => parseInt(guestTypeId, 10));

		// Není vybrán žádný host, který by měl slevu, takže vrátíme standardně cenu podle počtu hostů
		if (guestTypeIdsWithDiscounts.length === 0) {
			return currencyPrices[noOfGuests];
		}
		*/

		// Celková cena vypočítaná na základě slev jednotlivých typů hostů
		const roomPricePerOneGuest = currencyPrices[noOfGuests] / noOfGuests;
		let totalRoomPrice = 0;

		for (let guestTypeId in guests) {
			if (!guestTypes.hasOwnProperty(guestTypeId) || guestTypes[guestTypeId].exclude) {
				continue;
			}

			totalRoomPrice += guestTypes[guestTypeId].fixedDiscount
				? (roomPricePerOneGuest - guestTypes[guestTypeId].discount[currency.id]) * guests[guestTypeId]
				: roomPricePerOneGuest * guests[guestTypeId] * (1 - guestTypes[guestTypeId].discount / 100)
		}

		// Příplatky a slevy
		if (extraFees !== null) {
			for (const extraFee of extraFees) {
				if (typeof extraFee.price === 'object') {
					// Cena zadaná pevnou částkou
					if (extraFee.price[currency.id]) {
						totalRoomPrice += extraFee.price[currency.id] * (extraFee.daily ? noOfNights : 1);
					}
				} else {
					// Cena zadaná procentem z ceny ubytování
					totalRoomPrice += roomPricePerOneGuest * noOfGuests * extraFee.price / 100;
				}
			}
		}

		return Math.ceil(totalRoomPrice);
	}

	// Strava
	let defaultBoardBasePrice = 0;
	const defaultBoardBaseId = getDefaultBoardBase(boardBases);
	if (boardBases && defaultBoardBaseId && boardBases[defaultBoardBaseId].priceIncluded !== 1 && boardBases[defaultBoardBaseId].price[currency.id]) {
		defaultBoardBasePrice = boardBases[defaultBoardBaseId].price[currency.id] *
			(Number.isInteger(guests) ? guests : maxNoOfGuests);
	}

	// Příplatky a slevy
	let totalExtraFees = 0;
	if (extraFees !== null) {
		for (const extraFee of extraFees) {
			if (typeof extraFee.price === 'object') {
				// Cena zadaná pevnou částkou
				if (extraFee.price[currency.id]) {
					totalExtraFees += extraFee.price[currency.id] * (extraFee.daily ? noOfNights : 1);
				}
			} else {
				// Cena zadaná procentem z ceny ubytování
				totalExtraFees += currencyPrices[guests || maxNoOfGuests] * extraFee.price / 100;
			}
		}
	}

	// Poplatky
	let totalFees = 0;
	if (pricelistFees) {
		for (let pricelistFee of Object.keys(pricelistFees)) {
			if (pricelistFees[pricelistFee].onlineForm === 0) continue;

			// 1 = dospělý
			totalFees += pricelistFees[pricelistFee].price[currency.id][1] * (Number.isInteger(guests) ? guests : room.beds) * noOfNights;
		}
	}

	return currencyPrices[guests || maxNoOfGuests] + defaultBoardBasePrice + totalExtraFees + totalFees;
}

/**
 * Funkce vezme text z API, který se umisťuje na první záložce do hlavičky. Vytáhne z něj emaily a udělá z nich
 * odkazy.
 * @param headerText
 * @returns {Array}
 */
export function formatHeaderText(headerText) {
	const formattedText = [];
	const headerTextSplit = headerText.split(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi);

	for (let i in headerTextSplit) {
		if (!headerTextSplit.hasOwnProperty(i)) continue;

		const part = headerTextSplit[i];

		if (part.includes('@')) {
			formattedText.push(<a href={`mailto:${part}`} className="link-underline" key={i}>{part}</a>);

		} else {
			formattedText.push(part);
		}
	}

	return formattedText;
}

/**
 * Funkce přijímá seznam pokojů určitého typu a vrací možnost konfigurace postele.
 * @param roomOffer
 */
export function getBedConfiguration(roomOffer) {
	if (roomOffer.beds < 2) return null;

	let isSingle = false;
	let isDouble = false;

	for (let roomId of Object.keys(roomOffer.rooms)) {
		const room = roomOffer.rooms[roomId];

		if (!room.available) continue;

		if (room.twinBed) {
			isSingle = true;
		}

		if (room.doubleBed) {
			isDouble = true;
		}
	}

	if (isSingle && isDouble) {
		return BedConfig.VARIABLE;

	} else if (isSingle) {
		return BedConfig.TWIN;

	} else if (isDouble) {
		return BedConfig.DOUBLE;
	}
}

/**
 * Funkce vrátí počet nocí mezi daty.
 * @param arrival
 * @param departure
 * @param dateFormat
 */
export function getNoOfNights(arrival, departure, dateFormat = DATE_FORMAT) {
	const arrivalMoment = moment(arrival, dateFormat);
	const departureMoment = moment(departure, dateFormat);

	return departureMoment.diff(arrivalMoment, 'days');
}

export function getCurrency(currencies, code) {
	if (!currencies) return null;

	for (let currency of Object.values(currencies)) {
		if (currency.code === code) {
			return currency;
		}
	}

	return null;
}

/**
 * Funkce vrátí ID výchozí měny.
 * @param currencies
 * @returns int
 */
export function getDefaultCurrency(currencies) {
	if (!currencies) return null;

	for (let currency of Object.values(currencies)) {
		if (currency.default) {
			return currency;
		}
	}

	return null;
}

/**
 * Funkce vrátí, zda uživatel zvolil nějakou stravu.
 * @param {Object} formValues
 * @return {boolean}
 */
export function isSelectedBoardBase(formValues) {
	if (typeof formValues.boardBase === 'object') {
		for (let boardBase of Object.values(formValues.boardBase)) {
			for (let noOfBoardBases of Object.values(boardBase)) {
				if (noOfBoardBases > 0) {
					return true;
				}
			}
		}
	} else {
		return !!formValues.boardBase;
	}
	return false;
}

/**
 * Funkce vrátí celkové množství zvolené stravy.
 * @param {Object} formValues
 * @param {Object} guestTypes
 * @param {string} [boardBase]
 * @return {number}
 */
export function getNoOfSelectedBoardBase(formValues, guestTypes, boardBase) {
	if (typeof formValues.boardBase === 'object') {
		// Strava dle typu hosta
		return Object.keys(formValues.boardBase[boardBase])
			.reduce((accumulator, guestTypeId) =>
				// Do celkového množství stravy započítáme pouze kategorie hosta, které započítáváme i do ceny ubytování
				accumulator + (guestTypes.hasOwnProperty(guestTypeId) && guestTypes[guestTypeId].exclude ? 0 : formValues.boardBase[boardBase][guestTypeId]), 0);
	} else {
		// Stejná strava pro všechny hosty
		let totalNoOfSelectedBoardBases = 0;

		for (let roomType of Object.keys(formValues.rooms)) {
			// Pro každý vybraný typ pokoje
			for (let selectedRoom of formValues.rooms[roomType]) {
				// Pro každý vybraný konkrétní pokoj
				totalNoOfSelectedBoardBases += Object.keys(selectedRoom.guestTypes)
					.reduce((accumulator, guestTypeId) =>
						// Do celkového množství stravy započítáme pouze kategorie hosta, které započítáváme i do ceny ubytování
						accumulator + (guestTypes.hasOwnProperty(guestTypeId) && guestTypes[guestTypeId].exclude ? 0 : selectedRoom.guestTypes[guestTypeId]), 0);
			}
		}

		return totalNoOfSelectedBoardBases;
	}
}

/**
 * Funkce vrátí celkové množství stravy pro zvolenou kategorii hosta.
 * @param {Object} formValues
 * @param {string} guestType
 * @return {number}
 */
export function getNoOfSelectedBoardBasesByGuestType(formValues, guestType) {
	return Object.values(formValues.boardBase).reduce((accumulator, boardBase) => accumulator + boardBase[guestType], 0);
}

/**
 * Vrací celkový počet vybraných pokojů k rezervaci.
 *
 * @param {Object} formValues
 * @return {number}
 */
export function getNoOfSelectedRooms(formValues) {
	return Object.values(formValues.rooms).reduce((accumulator, roomTypes) => accumulator + roomTypes.length, 0);
}

/**
 * Funkce vrátí true, pokud jsou všechny poplatky zahrnuté v ceně.
 * @param pricelistFees
 * @returns {boolean}
 */
export function isPricelistFeesIncluded(pricelistFees) {
	return Object.values(pricelistFees).every(pricelistFee => pricelistFee.onlineForm === 1);
}

/**
 * Funkce vrátí true, pokud existuje alespoň poplatek, který je zahrnut v ceně
 * @param pricelistFees
 * @param included Parametr nastavný na false převrací na alespoň jeden poplatek, který NENÍ zahrnut v ceně
 * @returns {boolean}
 */
export function isAtLeastOnePricelistFeeIncluded(pricelistFees, included = true) {
	return Object.values(pricelistFees).some(pricelistFee => pricelistFee.onlineForm === (included ? 1 : 0));
}

/**
 * Vypočítá celkovou cenu poplatků dle počtu a typu hostů.
 * @param pricelistFees
 * @param rooms
 * @param noOfNights
 * @param currency
 * @param stayType
 * @param {boolean} included Sčítat poplatky, které se mají požadovat již v online rezervačním formuláři
 */
export function getTotalPricelistFees(pricelistFees, rooms, noOfNights, currency, stayType, included = true) {
	let totalPrice = 0;

	for (let roomType of Object.keys(rooms)) {
		// Pro každý vybraný typ pokoje
		for (let selectedRoom of rooms[roomType]) {
			// Pro každého hosta zkontrolovat, jestli se k němu nemá přípočítávat poplatek
			for (let guestType of Object.keys(selectedRoom.guestTypes)) {
				for (let pricelistFee of Object.keys(pricelistFees)) {
					// Pokud se jedná o lázeňský poplatek ("vacation") a je vybraný firemní pobyt, tak nezapočítáváme
					if (pricelistFee === StayType.VACATION && stayType !== StayType.VACATION) continue;

					// Pokud je vypnuté "POŽADOVAT JIŽ V ONLINE REZERVAČNÍM FORMULÁŘI", tak nezapočítávat do ceny
					if (pricelistFees[pricelistFee].onlineForm === (included ? 0 : 1)) continue;

					totalPrice += pricelistFees[pricelistFee].price[currency.id][guestType] * selectedRoom.guestTypes[guestType] * noOfNights;
				}
			}
		}
	}

	return totalPrice;
}


// WEBPACK FOOTER //
// ./src/apiHelpers.js